import { ArrowUp, FileText as CommandIcon, Paperclip, Square, X } from "lucide-react";
import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/atomic/SelectBox";
import { Textarea } from "@/components/atomic/Textarea";
import { useAgentRunMutations } from "@/hooks/agent/useAgentRunMutations";
import { useAgentRuns } from "@/hooks/agent/useAgentRuns";
import { useFileUpload } from "@/hooks/agent/useFileUpload";
import { useModels } from "@/hooks/agent/useModels";
import { useSendMessage } from "@/hooks/agent/useSendMessage";
import { useThreadCosts } from "@/hooks/agent/useThreadCosts";
import { useThreadMutations } from "@/hooks/agent/useThreadMutations";
import useAuth from "@/hooks/useAuth";
import { Command } from "@/models/agent/command";
import { Button } from "./Button";
import { CommandSelector } from "./CommandSelector";

interface AgentChatInputProps {
  placeholder?: string;
  className?: string;
  onSend?: () => void;
}

const AgentChatInput: React.FC<AgentChatInputProps> = ({
  placeholder = "Ask anything",
  className = "",
  onSend,
}) => {
  const { threadId } = useParams();
  const [messageInput, setMessageInput] = useState("");
  const [isComposing, setIsComposing] = useState(false);
  const [selectedCommand, setSelectedCommand] = useState<Command | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { handleSendMessage, isSending } = useSendMessage();
  const { replaceThread } = useThreadMutations();
  const { isAdmin } = useAuth();
  const { selectedFiles, setSelectedFiles, uploadAllFiles, isUploading } = useFileUpload();
  const {
    currentThread,
    models,
    selectedModelKey,
    setSelectedModelKey,
    selectedModelName,
    selectedReasoningEffort,
  } = useModels(threadId);

  const isNewConversation = !threadId;

  // hooksを使用してデータ取得
  const { threadCost } = useThreadCosts(threadId || null);
  const { latestAgentRun } = useAgentRuns(threadId || null);
  const { cancelAgent } = useAgentRunMutations(threadId || null);

  const isAgentRunning = latestAgentRun?.isRunning ?? false;

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    if (!files) return;

    setSelectedFiles(prev => [...prev, ...files]);
    event.target.value = "";
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async () => {
    if (!canSubmit || !selectedModelName) return;

    try {
      if (threadId) {
        const filePaths = await uploadAllFiles(`/workspace/${threadId}/data`);

        const trimmed = messageInput.trim();
        if (trimmed) {
          const fileText = filePaths.map(path => `[UploadedFile: ${path}]`).join("\n");
          const finalMessage = fileText
            ? `${messageInput.trim()}\n\n${fileText}`
            : messageInput.trim();
          await handleSendMessage(finalMessage, selectedModelName, selectedCommand?.name, selectedReasoningEffort);
          if (currentThread) {
            replaceThread(
              currentThread.updateMetadata({
                model_name: selectedModelName,
                reasoning_effort: selectedReasoningEffort ?? "",
              }),
            );
          }
        } else {
          toast.success("ファイルをアップロードしました。");
        }
      } else {
        await handleSendMessage(messageInput.trim(), selectedModelName, selectedCommand?.name, selectedReasoningEffort);
      }
    } catch (error) {
      console.error("Failed to send message:", error);
      return;
    }

    // 状態をクリア
    setMessageInput("");
    setSelectedFiles([]);
    setSelectedCommand(null);
    onSend?.();
  };

  const onCancel = async () => {
    if (!latestAgentRun?.isRunning) return;

    await cancelAgent(latestAgentRun.id);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Shift+Enter または Cmd+Enter で送信、Enter単体は改行
    if (e.key === "Enter" && (e.shiftKey || e.metaKey) && !isComposing) {
      e.preventDefault();
      onSubmit();
    }
  };

  const canSubmit = threadId
    ? (messageInput.trim() || selectedFiles.length) && !isAgentRunning && !isUploading && !isSending
    : messageInput.trim() && !isAgentRunning && !isUploading && !isSending;

  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  const handleCompositionEnd = () => {
    setIsComposing(false);
  };

  return (
    <div className={`space-y-4 relative ${className}`}>
      {/* Selected Files */}
      {selectedFiles.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedFiles.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="flex items-center gap-2 bg-gray-100 rounded-md px-3 py-1 text-sm"
            >
              <Paperclip className="h-3 w-3" />
              <span className="truncate max-w-[120px]">{file.name}</span>
              <button
                onClick={() => removeFile(index)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Text Input with Icons */}
      <div className="relative bg-white rounded-[20px] border border-gray-200 p-2.5 pt-4 max-h-[250px] flex flex-col mb-1">
        {/* Input Field */}
        <div className="flex-1 min-h-0 overflow-y-auto mb-3 px-2">
          <Textarea
            placeholder={placeholder}
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            className="w-full border-none shadow-none bg-transparent p-0 text-base placeholder:text-gray-400 focus-visible:ring-0 resize-none min-h-[24px] markdown"
            rows={1}
          />
        </div>

        {/* Buttons Row */}
        <div className="flex items-center justify-between flex-shrink-0">
          {/* Left Side Buttons */}
          <div className="flex items-center gap-2">
            {/* Attach Button */}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600 cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading || isAgentRunning || !threadId}
            >
              <Paperclip className="h-4 w-4" />
            </Button>

            {/* Command Selector */}
            {!selectedCommand && (
              <CommandSelector
                onSelect={setSelectedCommand}
                disabled={isUploading || isAgentRunning}
              />
            )}

            {/* Selected Command Badge */}
            {selectedCommand && (
              <div className="flex items-center gap-1.5 h-7 bg-blue-100 rounded-md px-2 text-sm">
                <CommandIcon className="h-3.5 w-3.5 text-blue-500" />
                <span className="text-blue-500 font-medium">{selectedCommand.name}</span>
                <button
                  onClick={() => setSelectedCommand(null)}
                  className="text-blue-500 hover:text-blue-400 cursor-pointer"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>

          {/* Right Side Icons */}
          <div className="flex items-center gap-2">
            {/* Model Selector */}
            {isAdmin && (
              <Select
                value={selectedModelKey}
                onValueChange={setSelectedModelKey}
              >
                <SelectTrigger className="border-none shadow-none bg-transparent h-8 px-2 text-sm text-gray-400 hover:text-gray-500 w-auto gap-1 focus:ring-0 cursor-pointer">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {models.map((model) => (
                    <SelectItem key={model.key} value={model.key}>
                      {model.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {isAgentRunning
              ? (
                <Button
                  size="sm"
                  className="h-8 w-8 p-0 rounded-full bg-[#6888E1] hover:bg-[#6888E1]/90 cursor-pointer"
                  onClick={onCancel}
                >
                  <Square className="scale-84 fill-[#ffffff]" />
                </Button>
              )
              : (
                <Button
                  size="sm"
                  className="h-8 w-8 p-0 rounded-full bg-[#6888E1] hover:bg-[#6888E1]/90 cursor-pointer"
                  onClick={onSubmit}
                  disabled={!canSubmit}
                >
                  <ArrowUp
                    className="h-4 w-4"
                    strokeWidth={3}
                  />
                </Button>
              )}
          </div>
        </div>
      </div>

      {/* コスト表示 - 新規会話でない場合のみ表示 */}
      {!isNewConversation && (
        <div className="absolute right-1 bottom-[-33px] text-xs text-gray-400">
          Cost : ${threadCost.getTotalCost().toFixed(4)}
        </div>
      )}

      {/* 非表示のファイル入力 */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        style={{ display: "none" }}
        accept="*/*"
        multiple
      />
    </div>
  );
};

export default AgentChatInput;
